/* eslint-disable @typescript-eslint/no-explicit-any */

// deps
import React, { useContext, useMemo } from "react";

// internal-deps
import { getCloudinaryUrl } from "@swiggy-private/js-utils";
import type { NRErrorInfo } from "@swiggy-private/js-utils";
import type { GetSwiggyHeadersResponsePayload, NativeBridge } from "@swiggy-private/native-bridge";
import { PLWidgetController, WidgetControllerContext } from "@swiggy-private/dls-react-mxn/dist";
import type { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";

// styles
import {
    ResponseContext,
    ScreenNames,
    IApiConfigs,
    useDigitalMenuConfig,
} from "@swiggy-private/portal-dineout-context";
import { enrichParentOrderListWidgetCards, hasCTAWidget } from "./helpers";
import { BottomWidget, Container, FlexContainer, OverLayError } from "./styles";
import { ErrorPageWrapper } from "../BillSummary/styles";
// constants
import { EMPTY_CART_SCREEN } from "../constants/cart";

// components
import ErrorComponent from "../ErrorComponent/ErrorComponent";
import { useAnalytics } from "../hooks";
import { UserCategory } from "../Menu/types";
import { PLWidgetRenderer } from "../PLWidgetRenderer";
import { isCurrentUserHost, isV2Config } from "../utils/analytics";

interface ICartProps {
    cards: IPLWidgetControllerCards[];
    popupConfigs?: IApiConfigs;
    sdk: NativeBridge;
    headers: GetSwiggyHeadersResponsePayload;
    clickAnalytics?: (clickObject: Record<string, any>) => void;
    impressionAnalytics?: (clickObject: Record<string, any>) => void;
    genericOnclick: (_?: any, extraParams?: Record<string, any>) => Promise<void>;
    onInvalidWidget: (widgetName?: string) => void;
    onWidgetError: (error: NRErrorInfo, widgetName?: string) => void;
    isCartEmpty?: boolean;
    emptyCartCtaHandler: VoidFunction;
    onEmptyPage: VoidFunction;
    /** Screen name for analytics */
    screenName: ScreenNames;
}
const Cart = ({
    cards,
    popupConfigs,
    sdk,
    headers,
    impressionAnalytics: defaultImpressionAnalytics,
    clickAnalytics: defaultClickAnalytics,
    genericOnclick,
    isCartEmpty,
    emptyCartCtaHandler,
    onInvalidWidget,
    onWidgetError,
    onEmptyPage,
    screenName,
}: ICartProps) => {
    const { state } = useContext(ResponseContext);
    const { config } = useDigitalMenuConfig();
    const { impressionAnalytics, clickAnalytics } = useAnalytics({
        screenName: ScreenNames.CART,
        defaultImpressionAnalytics,
        defaultClickAnalytics,
    });

    const { pin, tableNo, status } = state.pin.data;
    const userCategory = isCurrentUserHost(status) ? UserCategory.PRIMARY : UserCategory.SECONDARY;
    const enrichedCards = useMemo(() => {
        return enrichParentOrderListWidgetCards(cards, {
            pin,
            table_no: tableNo,
            user_category: userCategory,
            is_v2_config: isV2Config(config),
        });
    }, [cards, pin, tableNo, userCategory, config]);

    const contextValue = useMemo(() => {
        return {
            sdk,
            impressionAnalytics,
            clickAnalytics,
            headers,
            clickHandler: genericOnclick,
        };
    }, [clickAnalytics, genericOnclick, headers, impressionAnalytics, sdk]);

    const showBottomWidget = useMemo(() => hasCTAWidget(cards), [cards]);

    const OverlayWrapper = useMemo(
        () => (state.placeOrderFailure?.isFullScreenError ? OverLayError : React.Fragment),
        [state.placeOrderFailure?.isFullScreenError]
    );

    if (isCartEmpty) {
        return (
            <ErrorPageWrapper>
                <ErrorComponent
                    onCtaClick={emptyCartCtaHandler}
                    {...EMPTY_CART_SCREEN}
                    imgSrc={getCloudinaryUrl(EMPTY_CART_SCREEN.img, {
                        width: 220 * 2,
                        height: 200 * 2,
                    })}
                    showFullPage={false}
                />
            </ErrorPageWrapper>
        );
    }

    return (
        <Container>
            <FlexContainer>
                <WidgetControllerContext.Provider value={contextValue}>
                    <PLWidgetRenderer
                        cards={enrichedCards}
                        popupConfigs={popupConfigs}
                        screenName={screenName}
                        onInvalidWidget={onInvalidWidget}
                        onWidgetError={onWidgetError}
                        onEmptyPage={onEmptyPage}
                    />
                    {/* Overlay for error */}
                    {state.placeOrderFailure.error && (
                        <OverlayWrapper>
                            <PLWidgetController
                                onInvalidWidget={onInvalidWidget}
                                onWidgetError={onWidgetError}
                                onEmptyPage={onEmptyPage}
                                cards={state.placeOrderFailure.data?.success?.cards || []}
                            />
                        </OverlayWrapper>
                    )}
                </WidgetControllerContext.Provider>
                {showBottomWidget ? <BottomWidget /> : null}
            </FlexContainer>
        </Container>
    );
};

export default React.memo(Cart);
